// @flow

import * as React from 'react';

import { useField, useForm } from 'react-final-form';

import {
  ChoiceFieldGroup,
  ChoiceField,
  useBasicValidators,
} from '@gandi/form/FinalForm';
import { useTranslator, mapTags } from '@gandi/translate';
import { Alert, Base, Block, BlockContent, BlockHeading } from '@gandi/ui';

import {
  CERT_ALTNAMES_INPUT_NAME,
  CERT_NUMBER_ADDRESSES_INPUT_NAME,
  CERT_TYPE_INPUT_NAME,
  CERT_TYPE_PROTECT_INPUT_NAME,
  CERT_WARRANTY_INPUT_NAME,
} from '../../consts';
import type { FormValues } from '../../types';

export type TypeOptionProps = {
  readOnly?: boolean,
};

export function TypeOption({ readOnly }: TypeOptionProps): React.MixedElement {
  const { __ } = useTranslator();
  const {
    input: { value: type },
    // $FlowFixMe[prop-missing]
  } = useField(CERT_TYPE_INPUT_NAME, { subscription: { value: true } });
  const form = useForm<FormValues>();
  const { required } = useBasicValidators();

  const items = [
    {
      value: 'standard',
      label: __('Standard'),
    },
    {
      value: 'pro',
      label: __('Pro'),
    },
    {
      value: 'business',
      label: __('Business'),
    },
  ];

  const helpMap = {
    standard: __(
      'Validated and delivered rapidly, without the need of paper forms. Recommended for members-only areas, intranets, webmails, etc.',
    ),

    pro: __(
      'Adds a finincial warranty to your certification. Recommended for securing websites that are exposed to financial transactions.',
    ),
    business: __(
      'Provided in partnership with the CA, they offer the highest protection currently available with this type of certificate.',
    ),
  };

  const help = helpMap[type];

  const proOrBusinessCert = ['pro', 'business'].includes(type);

  React.useEffect(() => {
    let previousValues: void | FormValues;
    const unsubscribe = form.subscribe(
      ({ values }) => {
        if (!previousValues) {
          // nothing to do on first render
          previousValues = values;
          return;
        }
        if (
          previousValues[CERT_TYPE_INPUT_NAME] ===
          values?.[CERT_TYPE_INPUT_NAME]
        ) {
          // nothing to do when field not changed
          return;
        }

        // -- Cert type
        form.batch(() => {
          form.change(CERT_TYPE_PROTECT_INPUT_NAME, undefined);
          form.change(CERT_NUMBER_ADDRESSES_INPUT_NAME, undefined);
          form.change(CERT_ALTNAMES_INPUT_NAME, undefined);
          if (values?.[CERT_TYPE_INPUT_NAME] === 'standard') {
            form.change(CERT_WARRANTY_INPUT_NAME, undefined);
          }
        });

        previousValues = values;
      },
      { values: true },
    );

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const choiceGroupProps = {
    legend: __('Type of certificate'),
    layout: 'row',
  };

  let alertMessage;
  if (proOrBusinessCert) {
    alertMessage = {
      pro: mapTags(
        __(
          'The validation of a Pro certifiate requires more time to confirm your identity than a Standard certificate, because it requires a more in-depth verification of your identity through paper forms [[seeCompleteListLink:]]. Please note that these certificates are only for legal entities.',
        ),
        () => ({
          seeCompleteListLink: (
            <a
              href={__('https://docs.gandi.net/en/ssl/documents/index.html')}
              target="_blank"
              rel="noopener noreferrer"
            >
              {__('(see the complete list)')}
            </a>
          ),
        }),
      ),
      business: mapTags(
        __(
          'The  validation of a Business certifiate requires more time to confirm your identity than a Standard certificate, because it requires a more in-depth verification of your identity through paper forms [[seeCompleteListLink::]]. Please note that these certificates are only for legal entities.',
        ),
        () => ({
          seeCompleteListLink: (
            <a
              href={__('https://docs.gandi.net/en/ssl/documents/index.html')}
              target="_blank"
              rel="noopener noreferrer"
            >
              {__('(see the complete list)')}
            </a>
          ),
        }),
      ),
    }[type];
  }

  return (
    <>
      <Block>
        <BlockHeading>
          {__('What type of certificate do you want?')}
        </BlockHeading>
        <BlockContent>
          <Base marginBottom="small">
            <ChoiceFieldGroup
              name={CERT_TYPE_INPUT_NAME}
              validate={required}
              choiceGroupProps={choiceGroupProps}
            >
              {items.map(({ value, label }) => (
                <ChoiceField
                  key={value}
                  name={CERT_TYPE_INPUT_NAME}
                  value={value}
                  label={label}
                  disabled={readOnly}
                />
              ))}
            </ChoiceFieldGroup>
          </Base>

          <Base tagName="p" margin="none">
            {help}
          </Base>
        </BlockContent>
      </Block>

      {alertMessage && (
        <Alert
          title={__('Good to know!')}
          message={alertMessage}
          intent="info"
          marginTop="small"
        />
      )}
    </>
  );
}
